import { InjectionToken } from '@angular/core';
import { Member } from '@app/app/page-modules/programs/models/member.model';
import { Program, ProgramItem } from '@app/app/page-modules/programs/models/program.model';
import { CardsLayoutType, EditorContent, PlaylistCardShort, PlaylistLayoutType } from '@app/app/shared/models';
import { Page } from '@app/app/shared/models/page';
import { ObservableResult } from '@app/app/shared/store';

export type ProgramsSortBy = 'NAME' | 'STATUS' | 'COMPLETED_COUNT' | 'LEARNERS_COUNT';

export interface ProgramsRequest {
  page?: number;
  size?: number;
  term?: string;
  sortBy?: ProgramsSortBy;
}

export interface ProgramMainSectionRequest {
  uid?: string;
  title: string;
  content?: EditorContent[];
  cardsLayoutType?: CardsLayoutType;
  layoutType: PlaylistLayoutType;
  imageName?: string;
  imageContentLength?: number;
  cards?: string[];
}

export interface ProgramCreationRequest {
  mainSection: ProgramMainSectionRequest;
  pageId?: string;
  pageSectionId?: string;
  embedded?: {
    parentPlaylistId: string;
    sectionId: string;
  };
  targetGroups?: string[];
  languageCode?: string;
}

export interface CreateProgramResponse {
  item: Program;
  uploadUrl: string | null;
}

export interface ProgramUpdateRequest {
  mainSection: ProgramMainSectionRequest;
  standardSections?: ProgramSectionRequestUnion[];
}

export interface UpdateProgramResponse {
  uri: string;
  formattedUri: string;
  coverImageUploadUrl: string | null;
}

export type ProgramSectionType =
  | 'TEXT_AREA'
  | 'FEATURED'
  | 'CONTINUE_PLAYLISTS'
  | 'LATEST_PLAYLISTS'
  | 'FRAMEWORK_TRACKER'
  | 'CALLOUT_FEATURED'
  | 'UPCOMING'
  | 'YOUR_COLLECTIONS'
  | 'TABLEAU_DASHBOARD';

export interface ProgramSectionRequest {
  type: ProgramSectionType;
  title?: string;
  afterSection?: string;
}

export interface ProgramTextAreaSectionRequest extends ProgramSectionRequest {
  type: 'TEXT_AREA';
  content?: EditorContent[];
}

export interface ProgramFeaturedSectionRequest extends ProgramSectionRequest {
  type: 'FEATURED';
  items?: { playlistUid?: string; type: string; cardUid?: string }[];
  content?: EditorContent[];
  colour?: string;
}

export interface ProgramCalloutSectionRequest extends ProgramSectionRequest {
  type: 'CALLOUT_FEATURED';
  items?: { playlistUid?: string; type: string; cardUid?: string }[];
  content?: EditorContent[];
  colour?: string;
}

export interface ProgramFrameworkTrackerSectionRequest extends ProgramSectionRequest {
  type: 'FRAMEWORK_TRACKER';
  tags: string[];
}

export interface ProgramAutomaticSectionRequest extends ProgramSectionRequest {
  type: 'CONTINUE_PLAYLISTS' | 'LATEST_PLAYLISTS' | 'UPCOMING' | 'YOUR_COLLECTIONS' | 'TABLEAU_DASHBOARD';
}

export interface TableauDashboardToken {
  token: string;
}

export type ProgramSectionRequestUnion =
  | ProgramTextAreaSectionRequest
  | ProgramFeaturedSectionRequest
  | ProgramCalloutSectionRequest
  | ProgramFrameworkTrackerSectionRequest
  | ProgramAutomaticSectionRequest;

export interface ProgramSectionResponseItem {
  _id: string;
  uid?: string;
  header?: string;
  name?: string;
  title?: string;
  image?: string;
  thumbnail?: string;
  uri?: string;
  type?: string;
  v3CardType?: string;
}

export interface ProgramSectionResponse {
  uid?: string;
  items?: ProgramSectionResponseItem[];
}

export interface ProgramSectionBulkUpdateItem {
  programUid: string;
  uid: string;
  request: ProgramSectionRequestUnion;
  languageCode?: string;
}

export interface CreateProgramFromTemplateRequest {
  templateUid: string;
  in?: string;
  section?: string;
  languageCode?: string;
}

export interface CollectionMembersRequest {
  page?: number;
  size?: number;
  term?: string;
}

export interface CollectionContentSearchRequest {
  term?: string;
  type?: string;
  page?: number;
  size?: number;
}

export interface ProgramDataService {
  getPrograms(orgClientId?: string, params?: ProgramsRequest): ObservableResult<Page<ProgramItem>>;
  getProgramsAnalytics(orgClientId?: string, params?: ProgramsRequest): ObservableResult<Page<ProgramItem>>;
  getProgram(programUri: string, languageCode?: string, playlistPath?: string): ObservableResult<Program>;
  createProgram(request: ProgramCreationRequest): ObservableResult<CreateProgramResponse>;
  createProgramFromTemplate(request: CreateProgramFromTemplateRequest): ObservableResult<CreateProgramResponse>;
  updateProgram(programUid: string, request: ProgramUpdateRequest, languageCode?: string): ObservableResult<UpdateProgramResponse>;

  addProgramSection(
    programUid: string,
    request: ProgramSectionRequestUnion,
    languageCode?: string,
  ): ObservableResult<ProgramSectionResponse>;

  updateProgramSection(
    programUid: string,
    sectionUid: string,
    request: ProgramSectionRequestUnion,
    languageCode?: string,
  ): ObservableResult<ProgramSectionResponse>;

  bulkUpdateProgramSections(programUid: string, sections: ProgramSectionBulkUpdateItem[]): ObservableResult<void>;

  deleteProgramSection(programUid: string, sectionUid: string): ObservableResult<void>;

  reorderProgramSections(programUid: string, request: { sections: string[] }): ObservableResult<void>;

  publishProgram(programUid: string): ObservableResult<void>;
  unpublishProgram(programUid: string): ObservableResult<void>;

  archiveProgram(programUid: string): ObservableResult<void>;

  discardProgram(programUid: string): ObservableResult<void>;

  createCollection(request: ProgramCreationRequest): ObservableResult<CreateProgramResponse>;

  getAdminCollections(orgClientId?: string, params?: ProgramsRequest): ObservableResult<Page<ProgramItem>>;

  getCollections(orgClientId?: string, params?: ProgramsRequest): ObservableResult<Page<ProgramItem>>;

  getCollectionByUri(uri: string, inParent?: string, languageCode?: string): ObservableResult<Program>;

  getCollectionMembers(collectionId: string, params?: CollectionMembersRequest): ObservableResult<Page<Member>>;

  searchCollectionContent(collectionId: string, params?: CollectionContentSearchRequest): ObservableResult<Page<PlaylistCardShort>>;

  addCollectionSection(
    collectionUid: string,
    request: ProgramSectionRequestUnion,
    languageCode?: string,
  ): ObservableResult<ProgramSectionResponse>;

  updateCollectionSection(
    collectionUid: string,
    sectionUid: string,
    request: ProgramSectionRequestUnion,
    languageCode?: string,
  ): ObservableResult<ProgramSectionResponse>;

  bulkUpdateCollectionSections(collectionUid: string, sections: ProgramSectionBulkUpdateItem[]): ObservableResult<void>;

  deleteCollectionSection(collectionUid: string, sectionUid: string): ObservableResult<void>;

  reorderCollectionSections(collectionUid: string, request: { sections: string[] }): ObservableResult<void>;

  getCollectionTableauToken(collectionUid: string, sectionUid: string): ObservableResult<TableauDashboardToken | null>;
}

export const CORE_PROGRAM_DATA_SERVICE = new InjectionToken<ProgramDataService>('CORE_PROGRAM_DATA_SERVICE');
